import React, { useState, useRef, useEffect } from 'react';

const MarketplaceHeader = ({
  vendorTypes = [],
  searchTerm,
  setSearchTerm,
  onSearchSubmit,
  onCategoryFilter,
  selectedVendorType,
  cartCount = 0,
  wishlistCount = 0,
  userLocation,
  onChangeLocation,
  onNavigateHome,
  onOpenAccountDropdown,
  showAccountDropdown,
  onCustomerAccount,
  onVendorLogin,
  isVendorAuthed,
  vendorName,
  onVendorLogout,
  onOpenAgentRegister,
  onOpenCart,
  onOpenWishlist,
  onScrollToDeals,
  onScrollToTopSelling,
  onScrollToBrands,
  onScrollToStores,
  searchContext = 'global',
  searchContextLabel = '',
  searchSuggestions = { products: [], vendors: [], categories: [] },
  searchLoading = false,
  onSearchSelectProduct,
  onSearchSelectVendor,
  onSearchSelectCategory,
}) => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const currentCategoryLabel =
    vendorTypes.find((v) => v.type === selectedVendorType)?.label || 'All Categories';

  const hasSuggestions =
    (searchSuggestions?.products || []).length > 0 ||
    (searchSuggestions?.vendors || []).length > 0 ||
    (searchSuggestions?.categories || []).length > 0;

  const searchScopeTitle = searchContext === 'vendor'
    ? `Search ${searchContextLabel || 'this vendor'}`
    : searchContext === 'category'
      ? `Search ${searchContextLabel || 'this category'}`
      : 'Search SchoolVerse';

  return (
    <header className="bg-white shadow-sm sticky top-0 z-40 border-b border-gray-100" data-testid="marketplace-header">
      {/* Main header row */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
        <div className="flex items-center gap-2 md:gap-5 py-2 md:py-4">
          {/* Mobile hamburger */}
          <button
            onClick={() => setShowMobileMenu((s) => !s)}
            className="lg:hidden text-gray-700 p-1"
            aria-label="Menu"
            data-testid="mobile-menu-btn"
          >
            <i className="fa-solid fa-bars text-xl" />
          </button>

          {/* Logo */}
          <button
            onClick={onNavigateHome}
            className="flex items-center gap-2 flex-shrink-0"
            data-testid="header-logo"
          >
            <img src="/schoolverse-logo.png" alt="SchoolVerse" className="h-10 md:h-12 w-auto max-w-[150px] sm:max-w-[190px] object-contain" />
          </button>

          {/* Search bar */}
          <div className="flex-1 min-w-0 max-w-2xl relative" ref={dropdownRef}>
            <div className="flex items-stretch border-2 border-cyan-500 rounded-full overflow-hidden bg-white h-10 md:h-auto">
              <input
                type="text"
                value={searchTerm || ''}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => {}}
                onKeyDown={(e) => e.key === 'Enter' && onSearchSubmit && onSearchSubmit()}
                placeholder={searchContext === 'vendor'
                  ? `Search ${searchContextLabel || 'this vendor'} products...`
                  : searchContext === 'category'
                    ? `Search ${searchContextLabel || 'this category'}...`
                    : 'Search products, vendors and categories...'}
                data-testid="header-search-input"
                className="flex-1 min-w-0 px-2.5 md:px-4 py-1.5 md:py-2.5 text-sm outline-none placeholder:text-gray-400"
              />
              {/* Category dropdown - desktop only */}
              <div className="hidden md:block relative border-l border-gray-200">
                <button
                  onClick={() => setShowCategoryDropdown((s) => !s)}
                  data-testid="header-category-dropdown"
                  className="h-full px-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 whitespace-nowrap"
                >
                  <span className="max-w-[130px] truncate">{currentCategoryLabel}</span>
                  <i className="fa-solid fa-chevron-down text-[10px] text-gray-500" />
                </button>
                {showCategoryDropdown && (
                  <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-80 overflow-y-auto">
                    <button
                      onClick={() => {
                        onCategoryFilter && onCategoryFilter('');
                        setShowCategoryDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-cyan-50 flex items-center gap-2"
                    >
                      <span></span>
                      <span>All Categories</span>
                    </button>
                    {vendorTypes.map((cat) => (
                      <button
                        key={cat.type}
                        onClick={() => {
                          onCategoryFilter && onCategoryFilter(cat.type);
                          setShowCategoryDropdown(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-cyan-50 flex items-center gap-2"
                      >
                        <span>{cat.icon}</span>
                        <span className="truncate">{cat.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={onSearchSubmit}
                data-testid="header-search-btn"
                className="bg-cyan-500 hover:bg-cyan-700 text-white px-2.5 md:px-6 py-1.5 md:py-2.5 font-semibold text-sm flex items-center justify-center gap-2 transition-colors min-w-[42px] md:min-w-0"
              >
                <i className="fa-solid fa-magnifying-glass" />
                <span className="hidden md:inline">Search</span>
              </button>
            </div>

            {/* Context-aware live search suggestions */}
            {String(searchTerm || '').trim() && (searchLoading || hasSuggestions) && (
              <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-gray-200 rounded-2xl shadow-2xl z-[70] overflow-hidden">
                <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wide text-gray-500">{searchScopeTitle}</span>
                  {searchLoading && <span className="text-xs text-cyan-500">Searching…</span>}
                </div>
                {!searchLoading && (searchSuggestions.categories || []).length > 0 && (
                  <div className="p-2 border-b border-gray-100">
                    <p className="px-2 py-1 text-[10px] font-bold uppercase text-gray-400">Categories</p>
                    {(searchSuggestions.categories || []).slice(0, 4).map((cat, i) => (
                      <button key={`${cat.label}-${i}`} type="button" onClick={() => onSearchSelectCategory && onSearchSelectCategory(cat)} className="w-full text-left px-3 py-2 rounded-xl hover:bg-cyan-50 flex items-center gap-2 text-sm">
                        <i className={`fa-solid ${cat.icon || 'fa-tag'} text-cyan-500`} />
                        <span>{cat.label}</span>
                      </button>
                    ))}
                  </div>
                )}
                {!searchLoading && (searchSuggestions.products || []).length > 0 && (
                  <div className="p-2 border-b border-gray-100">
                    <p className="px-2 py-1 text-[10px] font-bold uppercase text-gray-400">Products</p>
                    {(searchSuggestions.products || []).slice(0, 5).map((product) => (
                      <button key={product.id} type="button" onClick={() => onSearchSelectProduct && onSearchSelectProduct(product)} className="w-full text-left px-3 py-2 rounded-xl hover:bg-cyan-50 flex items-center gap-3">
                        <img src={product.image_url || '/imax-logo.png'} alt="" className="w-9 h-9 rounded-lg object-cover bg-gray-100" />
                        <span className="min-w-0"><span className="block text-sm font-semibold text-gray-900 truncate">{product.name}</span><span className="block text-xs text-gray-500 truncate">{product.vendor_name || 'SchoolVerse vendor'}</span></span>
                      </button>
                    ))}
                  </div>
                )}
                {!searchLoading && (searchSuggestions.vendors || []).length > 0 && (
                  <div className="p-2">
                    <p className="px-2 py-1 text-[10px] font-bold uppercase text-gray-400">Vendors</p>
                    {(searchSuggestions.vendors || []).slice(0, 4).map((vendor) => (
                      <button key={vendor.id} type="button" onClick={() => onSearchSelectVendor && onSearchSelectVendor(vendor)} className="w-full text-left px-3 py-2 rounded-xl hover:bg-cyan-50 flex items-center gap-3">
                        <img src={vendor.image_url || '/imax-logo.png'} alt="" className="w-9 h-9 rounded-full object-cover bg-gray-100" />
                        <span className="min-w-0"><span className="block text-sm font-semibold text-gray-900 truncate">{vendor.name}</span><span className="block text-xs text-gray-500 truncate">{vendor.location || vendor.vendor_type || 'SchoolVerse vendor'}</span></span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-2 md:gap-5 flex-shrink-0">
            {/* Location */}
            {userLocation && (
              <button
                onClick={onChangeLocation}
                data-testid="header-location"
                className="hidden md:flex items-center gap-1 text-xs text-gray-600 hover:text-cyan-700"
              >
                <i className="fa-solid fa-location-dot text-cyan-500" />
                <span className="max-w-[100px] truncate">
                  {userLocation.name === 'all' ? 'All Areas' : userLocation.name}
                </span>
                <i className="fa-solid fa-chevron-down text-[9px]" />
              </button>
            )}

            {/* Account */}
            {isVendorAuthed ? (
              <div className="hidden md:flex items-center gap-2">
                <span className="text-xs text-gray-600">Hi, {vendorName}</span>
                <button
                  onClick={onVendorLogout}
                  data-testid="header-logout"
                  className="text-xs text-cyan-700 hover:text-cyan-800 font-semibold"
                >
                  Logout
                </button>
              </div>
            ) : (
              <button
                onClick={onOpenAccountDropdown}
                data-testid="header-account-btn"
                className="flex items-center gap-2 text-sm text-gray-700 hover:text-cyan-700 font-medium"
              >
                <i className="fa-regular fa-user text-lg" />
                <span className="hidden md:inline">Account</span>
                <i className="fa-solid fa-chevron-down text-[10px] hidden md:inline" />
              </button>
            )}

            {/* Wishlist */}
            <button
              onClick={onOpenWishlist}
              className="relative hidden sm:flex items-center gap-2 text-sm text-gray-700 hover:text-cyan-700"
              data-testid="header-wishlist"
              aria-label="Wishlist"
            >
              <div className="relative">
                <i className="fa-regular fa-heart text-lg" />
                <span className="absolute -top-2 -right-2 bg-cyan-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {wishlistCount}
                </span>
              </div>
              <span className="hidden md:inline">Wishlist</span>
            </button>

            {/* Cart */}
            <button
              onClick={onOpenCart}
              className="relative flex items-center gap-2 text-sm text-gray-700 hover:text-cyan-700"
              data-testid="header-cart"
              aria-label="Cart"
            >
              <div className="relative">
                <i className="fa-solid fa-bag-shopping text-lg" />
                <span className="absolute -top-2 -right-2 bg-cyan-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {cartCount}
                </span>
              </div>
              <span className="hidden md:inline">Cart</span>
            </button>
          </div>
        </div>

        {/* Secondary nav - desktop */}
        <nav className="hidden lg:flex items-center gap-6 pb-2 -mt-1 text-sm" data-testid="header-subnav">
          <button
            onClick={onScrollToDeals}
            className="text-red-600 font-bold hover:underline"
            data-testid="subnav-super-deals"
          >
            Super Deals
          </button>
          <button onClick={onScrollToTopSelling} className="text-gray-700 hover:text-cyan-700">
            New Arrivals
          </button>
          <button onClick={onScrollToTopSelling} className="text-gray-700 hover:text-cyan-700">
            Top Selling
          </button>
          <button onClick={onScrollToBrands} className="text-gray-700 hover:text-cyan-700">
            Shop by Brand
          </button>
          <button onClick={onScrollToStores} className="text-gray-700 hover:text-cyan-700">
            Vendors
          </button>
          <button className="text-gray-700 hover:text-cyan-700">Track Order</button>
          <button onClick={onOpenAgentRegister} className="ml-auto inline-flex items-center rounded-full bg-yellow-400 px-4 py-2 text-slate-900 font-bold hover:bg-yellow-300">
            <i className="fa-solid fa-store mr-1" /> Sell on SchoolVerse
          </button>
        </nav>
      </div>

      {/* Mobile menu drawer */}
      {showMobileMenu && (
        <div className="lg:hidden border-t border-gray-100 bg-white shadow-lg" data-testid="mobile-menu">
          <div className="px-4 py-3 space-y-2 max-h-[70vh] overflow-y-auto">
            <button
              onClick={() => {
                onScrollToDeals && onScrollToDeals();
                setShowMobileMenu(false);
              }}
              className="w-full text-left py-2 text-red-600 font-bold"
            >
              Super Deals
            </button>
            <button
              onClick={() => {
                onScrollToTopSelling && onScrollToTopSelling();
                setShowMobileMenu(false);
              }}
              className="w-full text-left py-2 text-gray-700"
            >
              New Arrivals
            </button>
            <button
              onClick={() => {
                onScrollToTopSelling && onScrollToTopSelling();
                setShowMobileMenu(false);
              }}
              className="w-full text-left py-2 text-gray-700"
            >
              Top Selling
            </button>
            <button
              onClick={() => {
                onScrollToBrands && onScrollToBrands();
                setShowMobileMenu(false);
              }}
              className="w-full text-left py-2 text-gray-700"
            >
              Shop by Brand
            </button>
            <button
              onClick={() => {
                onScrollToStores && onScrollToStores();
                setShowMobileMenu(false);
              }}
              className="w-full text-left py-2 text-gray-700"
            >
              Vendors
            </button>
            <button
              onClick={() => {
                onOpenAgentRegister && onOpenAgentRegister();
                setShowMobileMenu(false);
              }}
              className="w-full text-left py-2 text-cyan-800 font-bold"
            >
              Sell on SchoolVerse
            </button>
            <div className="border-t border-gray-100 pt-2">
              <p className="text-xs uppercase text-gray-500 mb-1">Categories</p>
              {vendorTypes.map((cat) => (
                <button
                  key={cat.type}
                  onClick={() => {
                    onCategoryFilter && onCategoryFilter(cat.type);
                    setShowMobileMenu(false);
                  }}
                  className="w-full text-left py-1.5 text-sm text-gray-700 flex items-center gap-2"
                >
                  <span>{cat.icon}</span>
                  <span>{cat.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default MarketplaceHeader;



